/**
 * Frame Search Panel - Full-Screen Search Takeover
 * 
 * Provides a Material-inspired full-panel search modal for finding frames
 * 
 * Features:
 * - Full-screen modal with dimmed background
 * - Debounced local search on window.searchBarJson
 * - Keyboard navigation (↑/↓/Enter/Esc)
 * - Focus trap and scroll lock
 * - Recent searches (localStorage)
 * - Accessible (WCAG 2.1 AA)
 * - Toggles .show-sidebar class to hide/show main sidebar
 * 
 * Usage:
 *   window.FrameSearchPanel.open()
 *   window.FrameSearchPanel.close()
 * 
 * VERSION: 1.3-SIDEBAR-TOGGLE
 */

console.log('[FSP] Frame Search Panel v1.3-SIDEBAR-TOGGLE loaded');

(function () {
  'use strict';

  // ============================================
  // Configuration
  // ============================================
  
  const CONFIG = {
    DEBOUNCE_MS: 250,
    RECENT_MAX: 5,
    RECENT_KEY: 'fsp_recent_v1'
  };

  // ============================================
  // State
  // ============================================
  
  let state = {
    isOpen: false,
    query: '',
    results: [],
    selectedIndex: -1,
    originalFocus: null,
    searchData: [],
    recentSearches: []
  };

  // ============================================
  // DOM References
  // ============================================
  
  let dom = {
    panel: null,
    scrim: null,
    sheet: null,
    input: null,
    clearBtn: null,
    backBtns: [],
    progress: null,
    recentSection: null,
    list: null,
    empty: null,
    triggerInput: null
  };

  // ============================================
  // Data & Search
  // ============================================
  
  /**
   * Load search data from window.searchBarJson
   * Format: [{brand, frames:[{name, handle}]}]
   * Creates flat searchable index
   */
  function loadSearchData() {
    if (!window.searchBarJson || !Array.isArray(window.searchBarJson)) {
      console.warn('[FSP] window.searchBarJson not found or invalid');
      return [];
    }

    const flatData = [];
    window.searchBarJson.forEach(brandObj => {
      const brand = brandObj.brand || '';
      if (brandObj.frames && Array.isArray(brandObj.frames)) {
        brandObj.frames.forEach(frame => {
          flatData.push({
            brand: brand,
            name: frame.name || '',
            handle: frame.handle || '',
            // Create searchable string (lowercase)
            searchText: `${brand} ${frame.name} ${frame.handle}`.toLowerCase()
          });
        });
      }
    });

    return flatData;
  }

  /**
   * Search the local dataset
   * @param {string} query - Search query
   * @returns {array} Filtered and grouped results
   */
  function search(query) {
    if (!query || query.trim().length === 0) {
      return [];
    }

    const normalizedQuery = query.toLowerCase().trim();
    const matches = state.searchData.filter(item => 
      item.searchText.includes(normalizedQuery)
    );

    // Group by brand
    const grouped = {};
    matches.forEach(item => {
      if (!grouped[item.brand]) {
        grouped[item.brand] = [];
      }
      grouped[item.brand].push(item);
    });

    // Convert to array format
    const results = Object.keys(grouped).map(brand => ({
      brand,
      items: grouped[brand]
    }));

    return results;
  }

  /**
   * Highlight matched text in a string
   * @param {string} text - Original text
   * @param {string} query - Query to highlight
   * @returns {string} HTML with <span class="fsp__match">
   */
  function highlightMatch(text, query) {
    if (!query || !text) return text;
    
    const normalizedText = text.toLowerCase();
    const normalizedQuery = query.toLowerCase();
    const index = normalizedText.indexOf(normalizedQuery);
    
    if (index === -1) return text;
    
    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);
    
    return `${before}<span class="fsp__match">${match}</span>${after}`;
  }

  // ============================================
  // Recent Searches (localStorage)
  // ============================================
  
  /**
   * Load recent searches from localStorage
   * @returns {array} Array of recent query strings
   */
  function loadRecentSearches() {
    try {
      const stored = localStorage.getItem(CONFIG.RECENT_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (err) {
      console.warn('[FSP] Could not load recent searches:', err);
      return [];
    }
  }

  /**
   * Save a query to recent searches
   * @param {string} query - Query to save
   */
  function saveRecentSearch(query) {
    if (!query || query.trim().length === 0) return;
    
    try {
      let recent = loadRecentSearches();
      // Remove if already exists
      recent = recent.filter(q => q !== query);
      // Add to front
      recent.unshift(query);
      // Limit to max
      recent = recent.slice(0, CONFIG.RECENT_MAX);
      // Save
      localStorage.setItem(CONFIG.RECENT_KEY, JSON.stringify(recent));
      state.recentSearches = recent;
    } catch (err) {
      console.warn('[FSP] Could not save recent search:', err);
    }
  }

  /**
   * Render recent searches chips
   */
  function renderRecentSearches() {
    if (!dom.recentSection) return;
    
    const recent = state.recentSearches;
    if (recent.length === 0) {
      dom.recentSection.hidden = true;
      return;
    }

    const html = `
      <div class="fsp__recent-title">Recent Searches</div>
      <div class="fsp__recent-chips">
        ${recent.map(q => `<button class="fsp__chip" data-query="${escapeHtml(q)}">${escapeHtml(q)}</button>`).join('')}
      </div>
    `;
    
    dom.recentSection.innerHTML = html;
    dom.recentSection.hidden = false;

    // Attach click handlers
    dom.recentSection.querySelectorAll('.fsp__chip').forEach(chip => {
      chip.addEventListener('click', () => {
        const query = chip.dataset.query;
        dom.input.value = query;
        handleSearch();
      });
    });
  }

  // ============================================
  // Rendering
  // ============================================
  
  /**
   * Render search results
   * @param {array} results - Grouped results array
   */
  function renderResults(results) {
    if (!dom.list || !dom.empty) return;

    // Show/hide empty state
    if (results.length === 0) {
      dom.list.innerHTML = '';
      dom.empty.hidden = false;
      dom.recentSection.hidden = true;
      
      // Analytics: empty state
      trackEvent('frame_search_empty', { query: state.query });
      return;
    }

    dom.empty.hidden = true;
    dom.recentSection.hidden = true;

    // Build HTML
    let html = '';
    results.forEach(group => {
      html += `<li class="fsp__group" role="presentation">${escapeHtml(group.brand)}</li>`;
      group.items.forEach((item, idx) => {
        const globalIdx = calculateGlobalIndex(results, group.brand, idx);
        html += `
          <li class="fsp__option" 
              role="option" 
              tabindex="-1"
              data-index="${globalIdx}"
              data-brand="${escapeHtml(item.brand)}"
              data-name="${escapeHtml(item.name)}"
              data-handle="${escapeHtml(item.handle)}">
            <div class="fsp__text">
              <div class="fsp__brand">${highlightMatch(item.brand, state.query)}</div>
              <div class="fsp__name">${highlightMatch(item.name, state.query)}</div>
            </div>
          </li>
        `;
      });
    });

    dom.list.innerHTML = html;

    // Attach click handlers
    dom.list.querySelectorAll('.fsp__option').forEach(option => {
      option.addEventListener('click', () => selectResult(option));
    });
  }

  /**
   * Calculate global index for keyboard navigation
   * (skips group headers)
   */
  function calculateGlobalIndex(results, targetBrand, itemIdx) {
    let idx = 0;
    for (let g = 0; g < results.length; g++) {
      if (results[g].brand === targetBrand) {
        return idx + itemIdx;
      }
      idx += results[g].items.length;
    }
    return idx;
  }

  /**
   * Update selected index visually
   * @param {number} index - New selected index
   */
  function updateSelection(index) {
    const options = dom.list.querySelectorAll('.fsp__option');
    
    // Remove old selection
    options.forEach(opt => {
      opt.setAttribute('aria-selected', 'false');
    });

    // Set new selection
    if (index >= 0 && index < options.length) {
      state.selectedIndex = index;
      const selected = options[index];
      selected.setAttribute('aria-selected', 'true');
      selected.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } else {
      state.selectedIndex = -1;
    }
  }

  // ============================================
  // Actions
  // ============================================
  
  /**
   * Open the panel
   */
  function open() {
    console.log('[FSP] open() called');
    
    if (state.isOpen) {
      console.log('[FSP] Panel already open, skipping');
      return;
    }

    // Save current focus
    state.originalFocus = document.activeElement;
    console.log('[FSP] Saved original focus:', state.originalFocus);

    // Hide the sidebar by removing .show-sidebar class
    const sidebar = document.getElementById('sidebarMenu');
    if (sidebar) {
      sidebar.classList.remove('show-sidebar');
      console.log('[FSP] Removed .show-sidebar class to hide sidebar');
    }

    // Load data if not loaded
    if (state.searchData.length === 0) {
      console.log('[FSP] Loading search data...');
      state.searchData = loadSearchData();
      console.log('[FSP] Loaded', state.searchData.length, 'items');
    }

    // Load recent searches
    state.recentSearches = loadRecentSearches();
    console.log('[FSP] Loaded', state.recentSearches.length, 'recent searches');

    // Show panel
    dom.panel.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden'; // Lock scroll
    console.log('[FSP] Panel displayed, scroll locked');

    // Focus input
    setTimeout(() => {
      dom.input.focus();
      console.log('[FSP] Input focused');
    }, 100);

    // Show recent searches if empty
    if (!dom.input.value || dom.input.value.trim() === '') {
      console.log('[FSP] Rendering recent searches');
      renderRecentSearches();
    }

    // Push history state for back button support
    if (!history.state || !history.state.fsp) {
      history.pushState({ fsp: true }, '');
      console.log('[FSP] History state pushed');
    }

    state.isOpen = true;

    // Analytics
    trackEvent('frame_search_open');
    console.log('[FSP] Panel open complete');
  }

  /**
   * Close the panel
   */
  function close() {
    console.log('[FSP] close() called');
    
    if (!state.isOpen) {
      console.log('[FSP] Panel already closed, skipping');
      return;
    }

    // Hide panel
    dom.panel.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = ''; // Unlock scroll
    console.log('[FSP] Panel hidden, scroll unlocked');

    // Show the sidebar again by adding .show-sidebar class
    const sidebar = document.getElementById('sidebarMenu');
    if (sidebar) {
      sidebar.classList.add('show-sidebar');
      console.log('[FSP] Added .show-sidebar class to restore sidebar');
    }

    // Restore focus
    if (state.originalFocus && typeof state.originalFocus.focus === 'function') {
      state.originalFocus.focus();
      console.log('[FSP] Focus restored to:', state.originalFocus);
    }

    // Clear results
    dom.list.innerHTML = '';
    dom.empty.hidden = true;
    dom.recentSection.hidden = true;
    console.log('[FSP] Results cleared');

    // Remove history state if it was added
    if (history.state && history.state.fsp) {
      history.back();
      console.log('[FSP] History state removed');
    }

    state.isOpen = false;

    // Analytics
    trackEvent('frame_search_close');
    console.log('[FSP] Panel close complete');
  }

  /**
   * Clear the search input
   * Panel remains open
   */
  function clearQuery() {
    dom.input.value = '';
    state.query = '';
    state.results = [];
    state.selectedIndex = -1;
    
    dom.list.innerHTML = '';
    dom.empty.hidden = true;
    dom.clearBtn.hidden = true;

    // Show recent searches
    renderRecentSearches();

    // Focus input
    dom.input.focus();

    // Analytics
    trackEvent('frame_search_clear');
  }

  /**
   * Select a result
   * @param {HTMLElement} option - The selected option element
   */
  function selectResult(option) {
    const brand = option.dataset.brand;
    const name = option.dataset.name;
    const handle = option.dataset.handle;

    console.log('[FSP] ==========================================');
    console.log('[FSP] Frame selected:', { brand, name, handle });
    console.log('[FSP] ==========================================');

    // Save to recent
    saveRecentSearch(state.query);

    // Populate sidebar input (desktop and mobile)
    const frameDisplayValue = `${brand} ${name}`;
    
    if (dom.triggerInput) {
      console.log('[FSP] Updating desktop sidebar input field value to:', frameDisplayValue);
      dom.triggerInput.value = frameDisplayValue;
      dom.triggerInput.setAttribute('data-handle', handle);
      console.log('[FSP] Set data-handle attribute to:', handle);
    }
    
    if (dom.triggerInputMobile) {
      console.log('[FSP] Updating mobile sidebar input field value to:', frameDisplayValue);
      dom.triggerInputMobile.value = frameDisplayValue;
      dom.triggerInputMobile.setAttribute('data-handle', handle);
      console.log('[FSP] Set mobile data-handle attribute to:', handle);
    }
    
    // Update the global state if it exists (from shop-by-color.js)
    if (window.state && typeof window.state === 'object') {
      console.log('[FSP] BEFORE update - window.state.handle:', window.state.handle);
      window.state.handle = handle;
      console.log('[FSP] AFTER update - window.state.handle:', window.state.handle);
      console.log('[FSP] Full window.state:', window.state);
    } else {
      console.error('[FSP] window.state is not available!');
    }
    
    // Trigger input events to notify existing logic (desktop and mobile)
    const inputEvent = new Event('input', { bubbles: true });
    const changeEvent = new Event('change', { bubbles: true });
    
    if (dom.triggerInput) {
      dom.triggerInput.dispatchEvent(inputEvent);
      dom.triggerInput.dispatchEvent(changeEvent);
      console.log('[FSP] Dispatched input/change events to desktop input');
    }
    
    if (dom.triggerInputMobile) {
      dom.triggerInputMobile.dispatchEvent(inputEvent);
      dom.triggerInputMobile.dispatchEvent(changeEvent);
      console.log('[FSP] Dispatched input/change events to mobile input');
    }

    // Call validateCurrent if it exists (from shop-by-color.js)
    // This is what triggers the green validation state
    if (window.validateCurrent && typeof window.validateCurrent === 'function') {
      console.log('[FSP] window.validateCurrent is available, calling it...');
      setTimeout(() => {
        console.log('[FSP] Executing validateCurrent() now');
        window.validateCurrent();
      }, 100);
    } else {
      console.error('[FSP] window.validateCurrent is NOT available!');
      console.log('[FSP] window object keys:', Object.keys(window).filter(k => k.includes('validate') || k.includes('state')));
    }

    // Call onFrameSelected if it exists (legacy support)
    if (window.onFrameSelected && typeof window.onFrameSelected === 'function') {
      console.log('[FSP] Calling window.onFrameSelected()');
      window.onFrameSelected({
        brand,
        name,
        handle
      });
    }

    // Analytics
    trackEvent('frame_search_select', { brand, name, handle });

    // Close panel
    console.log('[FSP] Closing panel...');
    close();
  }

  /**
   * Handle search input (debounced)
   */
  let searchTimeout = null;
  function handleSearch() {
    clearTimeout(searchTimeout);
    
    const query = dom.input.value;
    state.query = query;

    // Show/hide clear button
    dom.clearBtn.hidden = query.trim().length === 0;

    // Show progress
    dom.progress.hidden = false;

    searchTimeout = setTimeout(() => {
      // Perform search
      state.results = search(query);
      renderResults(state.results);
      
      // Hide progress
      dom.progress.hidden = true;

      // Reset selection
      state.selectedIndex = -1;

      // Analytics
      trackEvent('frame_search_type', { length: query.length });
    }, CONFIG.DEBOUNCE_MS);
  }

  // ============================================
  // Keyboard Navigation
  // ============================================
  
  /**
   * Handle keyboard events
   * @param {KeyboardEvent} e
   */
  function handleKeydown(e) {
    if (!state.isOpen) return;

    const options = dom.list.querySelectorAll('.fsp__option');
    const maxIndex = options.length - 1;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        // If there's text, clear it; if empty, close panel
        if (dom.input.value.trim().length > 0) {
          clearQuery();
        } else {
          close();
        }
        break;

      case 'ArrowDown':
        e.preventDefault();
        if (maxIndex >= 0) {
          const nextIndex = state.selectedIndex < maxIndex ? state.selectedIndex + 1 : 0;
          updateSelection(nextIndex);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (maxIndex >= 0) {
          const prevIndex = state.selectedIndex > 0 ? state.selectedIndex - 1 : maxIndex;
          updateSelection(prevIndex);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (state.selectedIndex >= 0 && options[state.selectedIndex]) {
          selectResult(options[state.selectedIndex]);
        } else if (options.length === 1) {
          // If only one result, select it
          selectResult(options[0]);
        }
        break;

      case 'Tab':
        // Keep focus within panel (basic focus trap)
        const focusable = dom.sheet.querySelectorAll('button, input, [tabindex]:not([tabindex="-1"])');
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
        break;
    }
  }

  // ============================================
  // Browser Back Button Support
  // ============================================
  
  /**
   * Handle popstate (back button)
   */
  function handlePopState(e) {
    if (state.isOpen && (!e.state || !e.state.fsp)) {
      // User pressed back, close panel
      close();
    }
  }

  // ============================================
  // Utilities
  // ============================================
  
  /**
   * Escape HTML to prevent XSS
   * @param {string} str
   * @returns {string}
   */
  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Send analytics event
   * @param {string} event - Event name
   * @param {object} payload - Event data
   */
  function trackEvent(event, payload = {}) {
    // Support various analytics platforms
    if (window.dataLayer) {
      window.dataLayer.push({
        event: event,
        ...payload
      });
    }
    
    // Console log for debugging
    console.log(`[FSP Analytics] ${event}`, payload);
  }

  // ============================================
  // Initialization
  // ============================================
  
  /**
   * Initialize the frame search panel
   */
  function init() {
    console.log('[FSP] init() called');
    
    // Get DOM references
    dom.panel = document.getElementById('frameSearchPanel');
    if (!dom.panel) {
      console.error('[FSP] Panel element #frameSearchPanel not found');
      return;
    }
    console.log('[FSP] Panel element found');

    dom.scrim = dom.panel.querySelector('.fsp__scrim');
    dom.sheet = dom.panel.querySelector('.fsp__sheet');
    dom.input = document.getElementById('fspInput');
    dom.clearBtn = dom.panel.querySelector('[data-fsp-clear]');
    dom.backBtns = Array.from(dom.panel.querySelectorAll('[data-fsp-back]'));
    dom.progress = dom.panel.querySelector('.fsp__progress');
    dom.recentSection = document.getElementById('fspRecent');
    dom.list = document.getElementById('fspList');
    dom.empty = document.getElementById('fspEmpty');
    dom.triggerInput = document.getElementById('selectedFrameField');
    dom.triggerInputMobile = document.getElementById('selectedFrameFieldMobile');

    console.log('[FSP] DOM references:', {
      panel: !!dom.panel,
      scrim: !!dom.scrim,
      sheet: !!dom.sheet,
      input: !!dom.input,
      clearBtn: !!dom.clearBtn,
      backBtns: dom.backBtns.length,
      triggerInput: !!dom.triggerInput,
      triggerInputMobile: !!dom.triggerInputMobile
    });

    // Attach event listeners
    
    // Back buttons (scrim + arrow)
    dom.backBtns.forEach(btn => {
      btn.addEventListener('click', close);
    });
    console.log('[FSP] Back buttons attached:', dom.backBtns.length);

    // Clear button
    if (dom.clearBtn) {
      dom.clearBtn.addEventListener('click', clearQuery);
      console.log('[FSP] Clear button attached');
    }

    // Search input
    if (dom.input) {
      dom.input.addEventListener('input', handleSearch);
      console.log('[FSP] Search input attached');
    }

    // Keyboard navigation
    document.addEventListener('keydown', handleKeydown);
    console.log('[FSP] Keyboard navigation attached');

    // Browser back button
    window.addEventListener('popstate', handlePopState);
    console.log('[FSP] Popstate handler attached');

    // Trigger input focus/click opens panel (desktop)
    if (dom.triggerInput) {
      dom.triggerInput.addEventListener('focus', open);
      dom.triggerInput.addEventListener('click', open);
      console.log('[FSP] Trigger input events attached to:', dom.triggerInput.id);
    } else {
      console.warn('[FSP] Trigger input #selectedFrameField not found');
    }

    // Trigger input focus/click opens panel (mobile)
    if (dom.triggerInputMobile) {
      dom.triggerInputMobile.addEventListener('focus', open);
      dom.triggerInputMobile.addEventListener('click', open);
      console.log('[FSP] Mobile trigger input events attached to:', dom.triggerInputMobile.id);
    } else {
      console.warn('[FSP] Mobile trigger input #selectedFrameFieldMobile not found');
    }

    console.log('[FSP] Frame Search Panel initialization complete');
  }

  // ============================================
  // Public API
  // ============================================
  
  window.FrameSearchPanel = {
    open,
    close,
    clearQuery,
    init
  };

  // Auto-initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

